import React, { useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import {
  BUSINESS_DAYS_NAME_MAP,
  CTA_SIZE,
  CTA_TYPE,
} from "../../../data/constants";
import CtaComponent from "../../../components/reusable-ui/cta";
import { Checkbox, Modal } from "@mui/material";
import { TimePicker } from "antd";
import moment from "moment";
import DescriptionText from "../../../components/reusable-ui/description-text";
import { CMS_DESCRIPTION_TEXTS } from "../../../data/cms-description-text";
import { isMobileDevice } from "../../../helpers/device-info-helper";

interface Props {
  daySelected?: any;
  onModalClose?: any;
  isTimelineEditMode?: boolean;
  onSaveCtaClick?: any;
  curDayTimeSlots?: any;
  tConvert?: any;
}

const TimelineManagementModal = (props: Props) => {
  const {
    daySelected,
    onModalClose,
    isTimelineEditMode,
    onSaveCtaClick,
    curDayTimeSlots,
    tConvert,
  } = props;
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<any>({});
  const [allDaysSelected, setAllDaysSelected] = useState<boolean>(false);
  const [timeSlotList, setTimeSlotList] = useState<any>([]);
  const [errorMessageList, setErrorMessageList] = useState<any>([]);
  const [errorMessageMap, setErrorMessageMap] = useState<any>([]);

  useEffect(() => {
    if (isTimelineEditMode) {
      setSelectedDays({ [daySelected.toLowerCase()]: true });
    }
  }, [daySelected]);

  useEffect(() => {
    if (isTimelineEditMode) setTimeSlotList(cloneDeep(curDayTimeSlots) || []);
    else if (!curDayTimeSlots?.length) {
      setTimeSlotList([
        { startTime: { hh: "", mm: "" }, endTime: { hh: "", mm: "" } },
      ]);
    }
  }, [curDayTimeSlots]);

  const onAddNewTimeSlotClick = () => {
    let dataToUpdate = cloneDeep(timeSlotList) || [];
    setTimeSlotList([
      ...dataToUpdate,
      { startTime: { hh: "", mm: "" }, endTime: { hh: "", mm: "" } },
    ]);
    setIsChangesMade(true);
  };

  const handleTimeSlotChange = (
    value: any,
    index: number,
    fieldToUpdate: string
  ) => {
    let updatedTimeline = timeSlotList?.length
      ? cloneDeep(timeSlotList[index])
      : {};
    updatedTimeline[fieldToUpdate] = value;
    let dataToUpdate = timeSlotList?.length
      ? timeSlotList?.map((timeline: any, ind: number) => {
          return ind === index ? updatedTimeline : timeline;
        })
      : [];
    setTimeSlotList(dataToUpdate);
    setIsChangesMade(true);
  };

  const onDeleteIconClick = (index: number) => {
    let dataToUpdate = timeSlotList?.length
      ? timeSlotList?.filter((timeline: any, ind: number) => {
          return ind !== index;
        })
      : [];
    if (!dataToUpdate?.length) {
      dataToUpdate = [
        { startTime: { hh: "", mm: "" }, endTime: { hh: "", mm: "" } },
      ];
    }
    setTimeSlotList(dataToUpdate);
    setIsChangesMade(true);
  };

  const compareTimes = (timeObj1: any, timeObj2: any) => {
    // 1 (t2 > t1), 0 (t2 = t1), -1 (t2 < t1)
    if (Number(timeObj1["hh"]) > Number(timeObj2["hh"])) {
      return -1;
    }
    if (Number(timeObj1["hh"]) < Number(timeObj2["hh"])) {
      return 1;
    }

    if (Number(timeObj1["mm"]) > Number(timeObj2["mm"])) {
      return -1;
    }
    if (Number(timeObj1["mm"]) < Number(timeObj2["mm"])) {
      return 1;
    }
    return 0;
  };

  const overlapCheck = (timeSlot1: any, timeSlot2: any) => {
    // (Start1 < End2) and (End1 > Start2)

    if (
      compareTimes(timeSlot1.startTime, timeSlot2.endTime) > 0 &&
      compareTimes(timeSlot1.endTime, timeSlot2.startTime) < 0
    ) {
      return true;
    }
    return false;
  };

  const validateTimeLines = () => {
    if (!timeSlotList) {
      return { isValid: false, errMsgList: [] };
    }
    let filteredTimeSlots: any = [];
    let isTimeEmpty = false;

    let isEmptyInterval = false;
    let isRangeInvalid = false;

    // removing empty timeslots.
    // finding partially filled time slots.
    // finding invalid time slot ranges.
    for (let index = 0; index < timeSlotList?.length; index++) {
      if (
        !timeSlotList[index]?.startTime["hh"] &&
        !timeSlotList[index]?.startTime["mm"] &&
        !timeSlotList[index]?.endTime["hh"] &&
        !timeSlotList[index]?.endTime["mm"]
      ) {
        // empty time slot
        // isTimeEmpty = true;
        continue;
      }

      if (
        !timeSlotList[index]?.startTime["hh"] ||
        !timeSlotList[index]?.startTime["mm"] ||
        !timeSlotList[index]?.endTime["hh"] ||
        !timeSlotList[index]?.endTime["mm"]
      ) {
        // partially filled slot
        let timeSlot: any = cloneDeep(timeSlotList[index]) || {};
        timeSlot.showError = true;
        filteredTimeSlots.push(timeSlot);
        isTimeEmpty = true;
        continue;
      }

      if (
        tConvert(timeSlotList[index]?.startTime) ===
        tConvert(timeSlotList[index]?.endTime, true)
      ) {
        // start time = end time
        let timeSlot: any = cloneDeep(timeSlotList[index]) || {};
        timeSlot.showError = true;
        filteredTimeSlots.push(timeSlot);
        isEmptyInterval = true;
        continue;
      }

      let timeSlot: any = cloneDeep(timeSlotList[index]) || {};
      if (compareTimes(timeSlot.startTime, timeSlot.endTime) < 0) {
        // invalid time slot range
        isRangeInvalid = true;
        timeSlot.showError = true;
      } else {
        // valid time slot range
        delete timeSlot.showError;
      }
      filteredTimeSlots.push(timeSlot);
    }

    let errMsgList: any = [];

    // No timelines added
    if (!filteredTimeSlots?.length) {
      errMsgList.push("Please add atleast one time slot.");
    }

    // contains some partially filled time slots
    if (isTimeEmpty) {
      errMsgList.push("Start Time and End Time are mandatory.");
    }

    // contains some partially filled time slots
    if (isEmptyInterval) {
      errMsgList.push(`Can't add empty intervals.`);
    }

    // some time slot ranges are invalid
    if (isRangeInvalid) {
      errMsgList.push("Please select a valid time range.");
    }

    let rangesOverLap = false;
    let dataToUpdate: any = cloneDeep(filteredTimeSlots);
    // checking for overlaping time slots
    for (let i = 0; i < filteredTimeSlots?.length - 1; i++) {
      for (let j = i + 1; j < filteredTimeSlots?.length; j++) {
        let timeSlot_i: any = cloneDeep(filteredTimeSlots[i]) || {};
        let timeSlot_j: any = cloneDeep(filteredTimeSlots[j]) || {};
        if (timeSlot_i?.showError || timeSlot_j?.showError) {
          continue;
        }
        if (overlapCheck(timeSlot_i, timeSlot_j)) {
          rangesOverLap = true;
          timeSlot_i.showError = true;
          timeSlot_j.showError = true;
          dataToUpdate[i] = timeSlot_i;
          dataToUpdate[j] = timeSlot_j;
        }
      }
    }

    if (rangesOverLap) {
      errMsgList.push("Time slots must not overlap.");
    }

    let errMsgMap: any = {};
    let isValid = true;
    if (Object.keys(selectedDays)?.length === 0) {
      errMsgMap.selectedDays = "Please select atleast one day.";
      isValid = false;
    }
    if (errMsgList?.length) {
      isValid = false;
    }

    if (!isValid) {
      return { isValid, errMsgList, errMsgMap, dataToUpdate };
    }
    return { isValid, dataToUpdate };
  };

  const onSaveTimelinesCtaClick = () => {
    const validatedData = validateTimeLines();
    if (validatedData?.isValid) {
      onSaveCtaClick(validatedData?.dataToUpdate, selectedDays);
    } else {
      setErrorMessageList(validatedData?.errMsgList);
      setErrorMessageMap(validatedData?.errMsgMap);
      setTimeSlotList(validatedData?.dataToUpdate || []);
    }
  };

  const onDayTileClick = (day: string) => {
    let dataToUpdate = cloneDeep(selectedDays) || {};
    if (!dataToUpdate[day]) {
      dataToUpdate[day] = true;
      if (Object.keys(dataToUpdate)?.length === 7) {
        setAllDaysSelected(true);
      }
    } else {
      delete dataToUpdate[day];
      setAllDaysSelected(false);
    }
    setSelectedDays(dataToUpdate);
    setIsChangesMade(true);
  };

  const onAllDaysSelectedClick = (val: boolean) => {
    if (!val) {
      setSelectedDays({});
    } else {
      let dataToUpdate: any = {};
      for (let day of Object.keys(BUSINESS_DAYS_NAME_MAP)) {
        dataToUpdate[day] = true;
      }
      setSelectedDays(dataToUpdate);
    }
    setAllDaysSelected(val);
    setIsChangesMade(true);
  };

  const convertStrToMoment = (
    { hh = "", mm = "" }: any,
    isEndTime: boolean = false
  ) => {
    if (!hh || !mm) {
      return null;
    }

    let hrsNum = Number(hh),
      minsNum = Number(mm);
    if (isEndTime) {
      minsNum = minsNum + 1;
      if (minsNum >= 60) {
        minsNum -= 60;
        hrsNum += 1;
      }
      if (hrsNum >= 24) {
        hrsNum -= 24;
      }
    }
    return moment(`${hrsNum}:${minsNum}`, "HH:mm");
  };

  const convertMomentToTimeObj = (val: any, isEndTime: boolean = false) => {
    if (!val) {
      return {
        hh: "",
        mm: "",
      };
    }
    let hourVal = val.hour();
    let minVal = val.minute();
    if (isEndTime) {
      minVal = minVal - 1;
      if (minVal < 0) {
        minVal += 60;
        hourVal -= 1;
      }
      if (hourVal < 0) {
        hourVal += 24;
      }
    }
    return {
      hh: `${hourVal.toLocaleString("en-US", { minimumIntegerDigits: 2 })}`,
      mm: `${minVal.toLocaleString("en-US", { minimumIntegerDigits: 2 })}`,
    };
  };

  return (
    <Modal open={true} onClose={onModalClose}>
      <section
        className={`absolute w-[636px] min-h-[390px] max-h-[668px] rounded-[8px] left-1/2
          -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white pb-[24px]
          sm:w-full sm:right-0 sm:left-0 sm:bottom-0 sm:top-auto
          sm:translate-x-0 sm:translate-y-0 sm:rounded-b-[0] 
        `}
      >
        <div
          className={`flex items-center justify-between mb-[6px] border-b border-[#DDDDDD] px-[24px] py-[12px]
            sm:pr-[16px] sm:pl-[6px]
          `}
        >
          <div className="flex items-center">
            <div
              className="rista-sprite ic-arrow-left cursor-pointer"
              onClick={onModalClose}
            ></div>
            <h1 className="text-22 font-semibold">
              {isTimelineEditMode ? "Edit Timeline" : "Add Timeline"}
            </h1>
          </div>
          <div className="flex items-center">
            {isMobileDevice() ? null : (
              <CtaComponent
                ctaText={"Cancel"}
                ctaType={CTA_TYPE.secondary_cta}
                ctaSize={CTA_SIZE.medium}
                onCtaClick={onModalClose}
                customClass={" mr-10"}
              ></CtaComponent>
            )}
            <CtaComponent
              ctaText={"Save Timeline"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
              onCtaClick={onSaveTimelinesCtaClick}
              customClass={" ml-10"}
              isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        </div>

        <div className="w-full min-h-[283px] max-h-[561px] overflow-y-auto px-[24px] py-[6px] sm:px-[16px]">
          <DescriptionText
            descriptionText={CMS_DESCRIPTION_TEXTS.itemScheduleView.addTimeline}
            customClass="text-13 text-black/40 mb-[32px] sm:mb-[20px]"
            numOfLines={1}
          />
          <div className="mt-[16px] flex items-center">
            <Checkbox
              checked={allDaysSelected}
              onChange={(e) => onAllDaysSelectedClick(!!e.target.checked)}
            />
            <span className="text-16 ml-[8px] font-medium min-w-fit">
              Select all days
            </span>
          </div>

          <div className="flex items-center mt-[10px] sm:flex-wrap">
            {Object.keys(BUSINESS_DAYS_NAME_MAP)?.map(
              (day: string, index: number) => {
                return (
                  <div
                    key={`day-tile-${index}`}
                    className={`h-[60px] w-[60px] mr-[20px] last:mr-0 text-16 rounded-[8px] cursor-pointer
                      uppercase hover:border-2 hover:border-[#9FCAFD] flex items-center justify-center
                      ${
                        allDaysSelected || selectedDays[day]
                          ? "border-2 border-[#0074FB] text-[#0074FB] bg-[#F1F7FE]"
                          : "border border-[#EBEBEB] bg-[#FAFAFA] text-[#666666]"
                      }
                      sm:h-[52px] sm:w-[52px] sm:mr-[12px] sm:mb-[8px] sm:last:mr-[12px]
                    `}
                    onClick={() => {
                      onDayTileClick(day);
                    }}
                  >
                    {day}
                  </div>
                );
              }
            )}
          </div>
          {errorMessageMap?.selectedDays ? (
            <p className="text-12 text-[#9A3131] font-normal mt-[6px]">
              {errorMessageMap?.selectedDays}
            </p>
          ) : null}

          {errorMessageList?.length ? (
            <div className="my-16 py-6 px-8 bg-[#9A3131] bg-opacity-20 rounded-[5px]">
              {errorMessageList?.map((errMsg: any, index: number) => {
                return (
                  <div className="flex items-center mb-[4px] last:mb-0">
                    <div className="rista-sprite ic-info-red mr-8"></div>
                    <p className="text-16 font-medium text-[#9A3131]">
                      {errMsg}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className={`${!!timeSlotList?.length ? "mt-[24px]" : ""}`}>
            {timeSlotList?.length
              ? timeSlotList?.map((timeline: any, index: number) => {
                  return (
                    <div
                      key={`timeSlot-item-${index}`}
                      className="flex items-end mb-[12px] last:mb-0"
                    >
                      <div className="flex items-center justify-between w-[calc(100%-38px)]">
                        <div className="mr-[8px] w-[calc(50%-8px)]">
                          <p
                            className={`text-14 text-[#656565] font-normal mb-[8px] leading-none`}
                          >
                            Start Time <span className="text-[#FF0000]">*</span>
                          </p>
                          <TimePicker
                            value={convertStrToMoment(timeline?.startTime)}
                            use12Hours
                            format="hh:mm A"
                            onChange={(val) =>
                              handleTimeSlotChange(
                                convertMomentToTimeObj(val),
                                index,
                                "startTime"
                              )
                            }
                            minuteStep={15}
                            size="large"
                            className={`timeSlot-time-picker rounded-[8px] w-full ${
                              timeline?.showError ? "border-[#9A3131]" : ""
                            }`}
                            popupClassName={`timeSlot-picker-dropdown`}
                          />
                        </div>

                        <div className="ml-[8px] w-[calc(50%-8px)]">
                          <p className="text-14 text-[#656565] font-normal mb-[8px] leading-none">
                            End Time <span className="text-[#FF0000]">*</span>
                          </p>
                          <TimePicker
                            value={convertStrToMoment(timeline?.endTime, true)}
                            use12Hours
                            format="hh:mm A"
                            onChange={(val) =>
                              handleTimeSlotChange(
                                convertMomentToTimeObj(val, true),
                                index,
                                "endTime"
                              )
                            }
                            minuteStep={15}
                            size="large"
                            className={`timeSlot-time-picker rounded-[8px] w-full ${
                              timeline?.showError ? "border-[#9A3131]" : ""
                            }`}
                            popupClassName={`timeSlot-picker-dropdown`}
                          />
                        </div>
                      </div>
                      {timeSlotList?.length > 1 ? (
                        <div
                          className="ic-delete-black rista-sprite my-[10px] ml-[18px]"
                          onClick={() => onDeleteIconClick(index)}
                        ></div>
                      ) : null}
                    </div>
                  );
                })
              : null}
          </div>

          <button
            className="text-16 font-semibold text-[#0074FB] cursor-pointer mt-[16px]"
            onClick={onAddNewTimeSlotClick}
          >
            + Add New Time Slot
          </button>
        </div>
      </section>
    </Modal>
  );
};

export default TimelineManagementModal;
